/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {GlobalSearchCriteriaService} from '../services/global-search-criteria.service';
import {QueryChipModel} from './models/query-chip.model';
import {DropdownCriterionSelectionModel} from '../dropdown-criteria-selection/models/dropdown-criterion-selection.model';
import {ResizeObserver} from 'resize-observer';

@Component({
    selector: 'app-query-chips-area',
    templateUrl: './query-chips-area.component.html',
    styleUrls: ['./query-chips-area.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QueryChipsAreaComponent implements OnInit, OnDestroy {
    @Input() retractChipsArea: boolean;
    @Output() removeChipEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    private observer: ResizeObserver;

    constructor(private globalSearchCriteriaService: GlobalSearchCriteriaService,
                private host: ElementRef,
                private zone: NgZone) {
    }

    ngOnInit(): void {
        this.observer = new ResizeObserver(entries => {
            this.zone.run(() => {
                this.globalSearchCriteriaService.queryChipsAreaWidth.next(entries[0].contentRect.width);
            });
        });
        this.observer.observe(this.host.nativeElement);
    }

    ngOnDestroy(): void {
        this.observer.unobserve(this.host.nativeElement);
    }

    get dropdownDataBySearchCriteriaCode(): Map<string, DropdownCriterionSelectionModel> {
        return this.globalSearchCriteriaService.getDropdownDataBySearchCriteriaCode().criteria;
    }

    removeChip(criteriaFilter: QueryChipModel): void {
        this.globalSearchCriteriaService.removeCriterionFilterFromQuery(criteriaFilter);
        this.globalSearchCriteriaService.enableCriterion({
            criterion: criteriaFilter.filter.criterion,
            criterionContext: criteriaFilter.filter.criterionContext,
            criterionDynamicContext: criteriaFilter.filter.criterionDynamicContext,
            criterionFilterInputType: criteriaFilter.filter.criterionFilterInputType
        });
        this.removeChipEmitter.emit(true);
    };

    getCurrentFiltersInQuery(): QueryChipModel[] {
        return this.globalSearchCriteriaService.getCriteriaFiltersInQuery();
    }

    formatCriteriaFilterToDisplay(criteria: QueryChipModel): string[] {
        const criterionContext = this.dropdownDataBySearchCriteriaCode.get(criteria.filter.criterionContext);
        const criterion = criterionContext?.subDropdown.get(criteria.filter.criterion);

        let result;
        if (criterionContext?.name && criterion?.name) {
            result = [
                criterionContext.name,
                this.getDynamicCriterionName(criterion.name, criteria.filter.criterionSuffix)
            ];
        }
        return result;
    }

    formatCriteriaFilterValueToDisplay(criteria: QueryChipModel): string[] {
        return [criteria.formattedValue];
    }

    getChipBackgroundColor(criteria: QueryChipModel): string {
        return this.dropdownDataBySearchCriteriaCode.get(criteria.filter.criterionContext)?.color;
    }

    getDataCy(index: number): string {
        return `query-chip-${index}`;
    }

    private getDynamicCriterionName(criterionName: string, criterionNameSuffix: string): string {
        return criterionNameSuffix ? `${criterionName} ${criterionNameSuffix}` : `${criterionName}`;
    }
}
